import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
	Loader2,
	KeyRound,
	ExternalLink,
	Plus,
	Check,
	Copy,
	Webhook,
	Trash2,
} from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

import { cn } from "../../lib/utils";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { identidadeDaRede } from "../../pages/Zernio/redes";
import ZernioWebhookModal from "../../pages/Zernio/WebhookModal";

// Conectar uma rede social pela Zernio, sem sair da tela de Conexões.
//
// A chave é da empresa: não existe mais uma chave da instalação servindo a
// todo mundo. Por isso o primeiro passo aqui é a chave — sem ela não há conta
// nenhuma para listar, e mandar a pessoa para outra tela só para colar uma
// linha é o tipo de desvio que faz o recurso não ser usado.

// O que dá para autorizar por aqui. Só quem recebe mensagem direta: conectar
// uma rede sem inbox encheria a lista de conta que nunca traz conversa.
const REDES = [
	{ chave: "whatsapp", rotulo: "WhatsApp Oficial" },
	{ chave: "instagram", rotulo: "Instagram" },
	{ chave: "facebook", rotulo: "Facebook" },
	{ chave: "telegram", rotulo: "Telegram" },
];

const ZernioConnectModal = ({ open, onClose }) => {
	const [status, setStatus] = useState(null);
	const [disponiveis, setDisponiveis] = useState([]);
	const [apiKey, setApiKey] = useState("");
	const [salvando, setSalvando] = useState(false);
	const [carregando, setCarregando] = useState(true);
	const [conectando, setConectando] = useState("");
	const [webhooks, setWebhooks] = useState([]);
	const [registrando, setRegistrando] = useState(false);
	const [eventosAbertos, setEventosAbertos] = useState(false);
	const [removendo, setRemovendo] = useState(false);

	const carregarStatus = useCallback(async () => {
		try {
			const { data } = await api.get("/zernio/status");
			setStatus(data);
			return data;
		} catch (err) {
			toastError(err);
			return null;
		}
	}, []);

	const carregarDisponiveis = useCallback(async () => {
		try {
			const { data } = await api.get("/zernio/accounts/available");
			setDisponiveis(data || []);
		} catch (err) {
			toastError(err);
		}
	}, []);

	// O que está registrado HOJE na Zernio, e não o que este sistema gostaria
	// que estivesse: é a única forma de saber se a mensagem chega na hora ou só
	// na sincronização, que roda a cada 10 minutos.
	const carregarWebhooks = useCallback(async () => {
		try {
			const { data } = await api.get("/zernio/webhooks");
			setWebhooks(data.webhooks || []);
		} catch (err) {
			toastError(err);
		}
	}, []);

	useEffect(() => {
		if (!open) return;

		let ativo = true;

		(async () => {
			setCarregando(true);
			const dados = await carregarStatus();

			if (!ativo) return;

			// Sem chave não adianta pedir a lista: a chamada volta erro e a tela
			// mostraria um alerta no lugar do campo que resolve o problema.
			if (dados?.configurada) {
				await carregarDisponiveis();
				await carregarWebhooks();
			}

			if (ativo) setCarregando(false);
		})();

		return () => {
			ativo = false;
		};
	}, [open, carregarStatus, carregarDisponiveis, carregarWebhooks]);

	const salvarChave = async () => {
		if (!apiKey.trim()) return;

		setSalvando(true);

		try {
			const { data } = await api.post("/zernio/credentials", {
				apiKey: apiKey.trim(),
			});

			toast.success("Chave salva.");
			(data?.feito || []).forEach((passo) => toast.success(passo));
			(data?.pendente || []).forEach((passo) => toast.warn(passo));

			setApiKey("");
			await carregarStatus();
			await carregarDisponiveis();
			await carregarWebhooks();
		} catch (err) {
			toastError(err);
		}

		setSalvando(false);
	};

	const removerChave = async () => {
		// eslint-disable-next-line no-alert
		if (
			!window.confirm(
				"Desligar a Zernio desta empresa? As contas param de receber e a busca por conversas novas cessa. Os atendimentos e o histórico continuam aqui, e religar é só gravar a chave de novo.",
			)
		) {
			return;
		}

		setRemovendo(true);

		try {
			const { data } = await api.delete("/zernio/credentials");

			toast.success(
				data?.contasDesativadas
					? `Zernio desligada. ${data.contasDesativadas} conta(s) parada(s).`
					: "Zernio desligada.",
			);

			setDisponiveis([]);
			setWebhooks([]);
			await carregarStatus();
		} catch (err) {
			toastError(err);
		}

		setRemovendo(false);
	};

	const registrarWebhook = async (eventos) => {
		setRegistrando(true);

		try {
			const { data } = await api.post("/zernio/webhook", { events: eventos });

			toast.success(
				data.criado
					? "Webhook registrado na Zernio."
					: "Eventos do webhook atualizados."
			);

			await carregarWebhooks();
		} catch (err) {
			toastError(err);
		}

		setRegistrando(false);
	};

	const apagarWebhook = async (webhook) => {
		// eslint-disable-next-line no-alert
		if (
			!window.confirm(
				"Apagar este webhook na Zernio? As mensagens deixam de chegar na hora e passam a depender da sincronização, que roda a cada 10 minutos."
			)
		) {
			return;
		}

		try {
			await api.delete(`/zernio/webhooks/${webhook._id}`);
			toast.success("Webhook apagado.");
			await carregarWebhooks();
		} catch (err) {
			toastError(err);
		}
	};

	const copiar = (texto) => {
		navigator.clipboard.writeText(texto);
		toast.success("Copiado.");
	};

	// Traz para o atendimento uma conta que já está na conta da Zernio.
	const conectar = async (conta) => {
		setConectando(conta.accountId);

		try {
			await api.post("/zernio/accounts", {
				accountId: conta.accountId,
				platform: conta.platform,
				name: conta.name,
				username: conta.username,
				profileId: conta.profileId,
			});

			toast.success(`${conta.name} conectado.`);
			await carregarDisponiveis();
		} catch (err) {
			toastError(err);
		}

		setConectando("");
	};

	// Autorizar uma rede que ainda não existe na conta da Zernio. A Zernio abre
	// o OAuth da rede e devolve para a tela de contas.
	const autorizarRede = async (plataforma) => {
		setConectando(plataforma);

		try {
			const { data } = await api.get(`/zernio/connect/${plataforma}`);

			if (data?.url) {
				window.open(data.url, "_blank", "noopener");
				toast.info(
					"Autorize na janela que abriu. Ao voltar, a conta aparece aqui."
				);
			}
		} catch (err) {
			toastError(err);
		}

		setConectando("");
	};

	const naoConectadas = disponiveis.filter((conta) => !conta.conectada);
	const jaConectadas = disponiveis.filter((conta) => conta.conectada);

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<KeyRound className="h-5 w-5 text-violet-500" />
						Conectar pela Zernio
					</DialogTitle>
					<DialogDescription>
						WhatsApp Oficial, Instagram, Facebook e Telegram numa chave só. As
						conversas caem em Atendimentos, com fila e chatbot como qualquer
						outra conexão.
					</DialogDescription>
				</DialogHeader>

				{carregando ? (
					<div className="flex items-center justify-center py-10">
						<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
					</div>
				) : (
					<div className="space-y-5">
						{/* Passo 1: a chave da empresa. */}
						<div className="space-y-1.5">
							<Label htmlFor="zernio-key">Chave da Zernio</Label>
							<div className="flex gap-2">
								<Input
									id="zernio-key"
									type="password"
									value={apiKey}
									autoComplete="off"
									placeholder={
										status?.apiKeyMascarada
											? `${status.apiKeyMascarada}  (já configurada)`
											: "sk_..."
									}
									onChange={(e) => setApiKey(e.target.value)}
								/>
								<Button
									onClick={salvarChave}
									disabled={!apiKey.trim() || salvando}
								>
									{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
									Salvar
								</Button>
							</div>
							<p className="text-xs leading-relaxed text-muted-foreground">
								É a chave da conta da <b>sua empresa</b> na Zernio, gerada no
								painel dela. Ao salvar, o sistema traz sozinho tudo o que
								houver lá: Instagram, Facebook, Telegram e o WhatsApp Oficial
								com os modelos aprovados — e ainda registra o webhook e busca
								as conversas que já existem.{" "}
								<a
									href="https://zernio.com"
									target="_blank"
									rel="noreferrer"
									className="inline-flex items-center gap-1 underline underline-offset-2"
								>
									Abrir a Zernio
									<ExternalLink className="h-3 w-3" />
								</a>
							</p>
						</div>

						{!status?.configurada && (
							<div className="rounded-lg border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
								Enquanto não houver chave, esta empresa não fala com a Zernio —
								nenhuma rede social entra no atendimento.
							</div>
						)}

						{status?.configurada && (
							<>
								{/* Passo 2: trazer para o atendimento o que já existe lá. */}
								<div className="space-y-2">
									<p className="text-sm font-medium">
										Contas na sua Zernio
										{naoConectadas.length > 0 && (
											<span className="ml-1 font-normal text-muted-foreground">
												({naoConectadas.length} para conectar)
											</span>
										)}
									</p>

									{disponiveis.length === 0 && (
										<p className="text-xs text-muted-foreground">
											Nenhuma conta na sua Zernio ainda. Autorize uma rede
											abaixo.
										</p>
									)}

									{disponiveis.length > 0 && naoConectadas.length === 0 && (
										<p className="text-xs text-muted-foreground">
											Tudo o que havia na sua Zernio já está no atendimento.
										</p>
									)}

									{naoConectadas.map((conta) => {
										const rede = identidadeDaRede(conta.platform);
										const Glifo = rede.icone;

										return (
											<div
												key={conta.accountId}
												className="flex items-center gap-3 rounded-lg border p-3"
											>
												<span
													className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white"
													style={{ backgroundColor: rede.corSelo }}
												>
													<Glifo className="h-4 w-4" />
												</span>
												<div className="min-w-0 flex-1">
													<p className="truncate text-sm font-medium">
														{conta.name}
													</p>
													<p className="truncate text-xs text-muted-foreground">
														{rede.rotulo}
														{conta.username ? ` · ${conta.username}` : ""}
													</p>
												</div>
												<Button
													size="sm"
													onClick={() => conectar(conta)}
													disabled={conectando === conta.accountId}
												>
													{conectando === conta.accountId && (
														<Loader2 className="h-4 w-4 animate-spin" />
													)}
													Conectar
												</Button>
											</div>
										);
									})}

									{jaConectadas.map((conta) => {
										const rede = identidadeDaRede(conta.platform);
										const Glifo = rede.icone;

										return (
											<div
												key={conta.accountId}
												className="flex items-center gap-3 rounded-lg border border-dashed p-3 opacity-70"
											>
												<span
													className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white"
													style={{ backgroundColor: rede.corSelo }}
												>
													<Glifo className="h-4 w-4" />
												</span>
												<div className="min-w-0 flex-1">
													<p className="truncate text-sm font-medium">
														{conta.name}
													</p>
													<p className="truncate text-xs text-muted-foreground">
														{rede.rotulo}
													</p>
												</div>
												<span className="flex items-center gap-1 text-xs text-emerald-600">
													<Check className="h-3.5 w-3.5" />
													Já conectada
												</span>
											</div>
										);
									})}
								</div>

								{/* Entrega em tempo real. Fica junto da chave porque é o
								    mesmo assunto: sem o webhook a conta conecta e a
								    conversa só aparece dez minutos depois. */}
								{status?.webhookUrl && (
									<div className="space-y-2 border-t pt-4">
										<p className="flex items-center gap-2 text-sm font-medium">
											<Webhook className="h-4 w-4 text-muted-foreground" />
											Entrega em tempo real
										</p>
										<p className="text-xs text-muted-foreground">
											É por este endereço que a mensagem chega na hora. Sem
											ele, a conversa só aparece na sincronização, que roda a
											cada 10 minutos.
										</p>

										<div className="flex items-center gap-2">
											<code className="min-w-0 flex-1 truncate rounded bg-muted/60 px-2 py-1.5 text-xs">
												{status.webhookUrl}
											</code>
											<Button
												variant="outline"
												size="icon"
												onClick={() => copiar(status.webhookUrl)}
												aria-label="Copiar endereço do webhook"
											>
												<Copy className="h-4 w-4" />
											</Button>
										</div>

										{webhooks.map((webhook) => (
											<div
												key={webhook._id}
												className="flex items-center gap-2 rounded-lg border p-3"
											>
												<span
													className={cn(
														"flex h-2 w-2 shrink-0 rounded-full",
														webhook.isActive ? "bg-emerald-500" : "bg-amber-500"
													)}
												/>
												<div className="min-w-0 flex-1">
													<p className="truncate text-sm font-medium">
														{webhook.name}
													</p>
													<p className="truncate text-xs text-muted-foreground">
														{(webhook.events || []).length} eventos ·{" "}
														{webhook.isActive ? "ativo" : "desativado"}
														{webhook.failureCount
															? ` · ${webhook.failureCount} falhas seguidas`
															: ""}
													</p>
												</div>
												<Button
													variant="ghost"
													size="icon"
													onClick={() => apagarWebhook(webhook)}
													aria-label="Apagar webhook"
												>
													<Trash2 className="h-4 w-4 text-destructive" />
												</Button>
											</div>
										))}

										<Button
											variant="outline"
											size="sm"
											onClick={() => setEventosAbertos(true)}
											disabled={registrando || !status?.webhookConfigurado}
										>
											{registrando ? (
												<Loader2 className="h-4 w-4 animate-spin" />
											) : (
												<Webhook className="h-4 w-4" />
											)}
											{webhooks.length ? "Escolher eventos" : "Registrar na Zernio"}
										</Button>

										{!status?.webhookConfigurado && (
											<p className="text-xs text-amber-600 dark:text-amber-400">
												O segredo do webhook é gerado ao salvar a chave. Salve a
												chave antes de registrar — sem segredo, toda mensagem
												que chegar é recusada.
											</p>
										)}
									</div>
								)}

								{/* Passo 3: autorizar uma rede nova dentro da Zernio. */}
								<div className="space-y-2 border-t pt-4">
									<p className="text-sm font-medium">Autorizar uma rede nova</p>
									<p className="text-xs text-muted-foreground">
										Abre a autorização da rede na Zernio. Ao voltar, a conta
										aparece na lista acima.
									</p>
									<div className="flex flex-wrap gap-2">
										{REDES.map((rede) => {
											const identidade = identidadeDaRede(rede.chave);
											const Glifo = identidade.icone;

											return (
												<Button
													key={rede.chave}
													variant="outline"
													size="sm"
													onClick={() => autorizarRede(rede.chave)}
													disabled={conectando === rede.chave}
												>
													{conectando === rede.chave ? (
														<Loader2 className="h-4 w-4 animate-spin" />
													) : (
														<Glifo
															className="h-4 w-4"
															style={{ color: identidade.corSelo }}
														/>
													)}
													{rede.rotulo}
													<Plus className="h-3 w-3 opacity-60" />
												</Button>
											);
										})}
									</div>
								</div>
							</>
						)}
					</div>
				)}

				<DialogFooter className="sm:justify-between">
					{/* Sem uma saída, tirar a Zernio do ar exigia mexer no banco — e a
					    busca por conversas novas seguia batendo na API a cada 15s por
					    uma conta que ninguém usa mais. */}
					{status?.configurada ? (
						<Button
							variant="ghost"
							className="text-destructive hover:text-destructive"
							onClick={removerChave}
							disabled={removendo}
						>
							{removendo ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Trash2 className="h-4 w-4" />
							)}
							Remover a chave
						</Button>
					) : (
						<span />
					)}
					<Button variant="outline" onClick={onClose}>
						Fechar
					</Button>
				</DialogFooter>
			</DialogContent>

			<ZernioWebhookModal
				open={eventosAbertos}
				url={status?.webhookUrl}
				eventosAtuais={webhooks[0]?.events}
				onClose={() => setEventosAbertos(false)}
				onRegistrar={registrarWebhook}
			/>
		</Dialog>
	);
};

export default ZernioConnectModal;
